import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useNavigate } from '@tanstack/react-router'
import { endOfDay, startOfDay, subMonths } from 'date-fns'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { TableCellsIcon } from '@gto/icons-react/outline/table-cells'
import { UsersIcon } from '@gto/icons-react/outline/users'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { DateRangePicker } from '@gto/ui/date-picker'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'
import { Typography } from '@gto/ui/typography'

import type { AffiliateAnalyticsRowSchema } from '@/api/gen'
import { useGetProgramAnalyticsView, useGetProgramStatsView } from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { PerformanceChart } from '@/components/performance-chart'
import { StatsCard } from '@/components/stats-card'
import { fluidColumn } from '@/components/table-columns'
import { useDateShortcuts } from '@/hooks/use-date-shortcuts'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { toDateStr } from '@/utils/date-str'
import type { ReportGranularity } from '@/utils/report-period'
import { isReportGranularity, periodWindow } from '@/utils/report-period'
import { toAmount } from '@/utils/to-amount'
import { toNullablePct } from '@/utils/to-nullable-pct'

const useReportColumns = (): ColumnDef<AffiliateAnalyticsRowSchema & { id: string }>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAnalyticsRowSchema & { id: string }>([
    {
      accessorKey: 'period_label',
      header: formatMessage({ id: 'reports.period' }),
      ...fluidColumn({ min: 140, flex: 1 }),
      enableSorting: false,
      cell: ({ getValue }) => String(getValue()),
    },
    {
      accessorKey: 'orders',
      header: formatMessage({ id: 'reports.orders' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'unique_customers',
      header: formatMessage({ id: 'reports.customers' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'registrations',
      header: formatMessage({ id: 'reports.registrations' }),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => <FormattedNumber value={Number(getValue() ?? 0)} />,
    },
    {
      accessorKey: 'revenue_usd',
      header: formatMessage({ id: 'reports.revenue' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      // No `Typography` wrapper: the DS cell base already renders `body2`, so one that carries
      // no other prop only adds a span. The commission cell below keeps its wrapper because it
      // sets `color`.
      cell: ({ row }) => (
        <FormattedNumber
          currency="USD"
          maximumFractionDigits={0}
          style="currency"
          value={toAmount(row.original.revenue_usd, 'reports table revenue_usd:')}
        />
      ),
    },
    {
      accessorKey: 'commission_usd',
      header: formatMessage({ id: 'reports.commissions' }),
      ...fluidColumn({ min: 100, flex: 0.7, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="primary"
          variant="body2"
        >
          <FormattedNumber
            currency="USD"
            maximumFractionDigits={0}
            style="currency"
            value={toAmount(row.original.commission_usd, 'reports table commission_usd:')}
          />
        </Typography>
      ),
    },
    {
      id: 'cost_pct',
      // Derived, not a wire field — `accessorFn` replaces the old `valueGetter`.
      accessorFn: (row) => {
        const rev = toAmount(row.revenue_usd, 'reports cost_pct revenue_usd:')
        const comm = toAmount(row.commission_usd, 'reports cost_pct commission_usd:')

        return rev > 0 ? (comm / rev) * 100 : 0
      },
      header: formatMessage({ id: 'reports.costPct' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      enableSorting: false,
      cell: ({ getValue }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedNumber
            maximumFractionDigits={1}
            value={Number(getValue() ?? 0)}
          />
          %
        </Typography>
      ),
    },
  ])
}

export const AdminReportsPage = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const shortcuts = useDateShortcuts()

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => [
    subMonths(new Date(), 1),
    new Date(),
  ])
  const [granularity, setGranularity] = useState<ReportGranularity>('daily')

  const [from, to] = dateRange
  const dateParams = {
    date_from: from ? toDateStr(from) : toDateStr(subMonths(new Date(), 1)),
    date_to: to ? toDateStr(to) : toDateStr(new Date()),
  }

  // Stats power the KPI tiles' period deltas — the BE computes *_delta_pct only
  // when both dates are supplied, which matches the picker's selected window.
  const statsQuery = useGetProgramStatsView(dateParams)
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery
  const analyticsQuery = useGetProgramAnalyticsView({
    ...dateParams,
    granularity,
  })
  const {
    data: analytics,
    isLoading: analyticsLoading,
    isError: analyticsError,
    refetch: refetchAnalytics,
  } = analyticsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(statsQuery, 'getProgramStatsView failed: ')
  useReportQueryError(analyticsQuery, 'getProgramAnalyticsView failed: ')

  // Analytics rows arrive newest-first; charts and sparklines read
  // chronologically (oldest on the left), so reverse a copy here.
  const analyticsRows = [...(analytics?.rows ?? [])].reverse()
  const ordersSeries = analyticsRows.map((row) => row.orders)
  const revenueSeries = analyticsRows.map((row) =>
    toAmount(row.revenue_usd, 'reports analytics revenue_usd:'),
  )
  const commissionsSeries = analyticsRows.map((row) =>
    toAmount(row.commission_usd, 'reports analytics commission_usd:'),
  )

  const totalRevenue = toAmount(stats?.total_revenue_usd, 'reports total_revenue_usd:')
  const totalCommissions = toAmount(stats?.total_commissions_usd, 'reports total_commissions_usd:')

  const chartData = analyticsRows.map((row) => ({
    label: row.period_label,
    revenue: toAmount(row.revenue_usd, 'reports analytics revenue_usd:'),
    commissions: toAmount(row.commission_usd, 'reports analytics commission_usd:'),
  }))

  const columns = useReportColumns()
  // Table reads most-recent-first, so keep the API order (cache array untouched).
  const rows = (analytics?.rows ?? []).map((row) => ({
    ...row,
    id: `${row.period_start}-${row.period_label}`,
  }))

  const granularityToggle = (
    <ToggleButtonGroup
      size="small"
      value={granularity}
      onValueChange={(v) => {
        if (isReportGranularity(v)) {
          setGranularity(v)
        }
      }}
    >
      <ToggleButton value="daily">
        {formatMessage({ id: 'dashboard.granularity.daily' })}
      </ToggleButton>
      <ToggleButton value="weekly">
        {formatMessage({ id: 'dashboard.granularity.weekly' })}
      </ToggleButton>
      <ToggleButton value="monthly">
        {formatMessage({ id: 'dashboard.granularity.monthly' })}
      </ToggleButton>
      <ToggleButton value="yearly">
        {formatMessage({ id: 'dashboard.granularity.yearly' })}
      </ToggleButton>
    </ToggleButtonGroup>
  )

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="reports.title" />}
        actions={
          <DateRangePicker
            closeOnSelect={false}
            presets={shortcuts}
            value={dateRange}
            onChange={([newFrom, newTo], context) => {
              if (context.validationError.some(Boolean)) {
                return
              }
              setDateRange([newFrom ? startOfDay(newFrom) : null, newTo ? endOfDay(newTo) : null])
            }}
          />
        }
      />

      <Page.Content>
        {statsError || analyticsError ? (
          <InlineQueryError
            data-tst="reports-error"
            messageId="reports.error"
            onRetry={() => {
              if (statsError) {
                void refetchStats()
              }
              if (analyticsError) {
                void refetchAnalytics()
              }
            }}
          />
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<ShoppingCartIcon />}
                label={<FormattedMessage id="reports.totalOrders" />}
                loading={statsLoading}
                sparklineData={ordersSeries}
                tooltip={formatMessage({ id: 'tooltip.totalOrders' })}
                value={<FormattedNumber value={stats?.total_orders ?? 0} />}
                deltaPct={toNullablePct(
                  stats?.orders_delta_pct,
                  'admin-reports toNullablePct orders_delta_pct:',
                )}
              />
              <DashboardKpiTile
                icon={<CurrencyDollarIcon />}
                label={<FormattedMessage id="reports.totalRevenue" />}
                loading={statsLoading}
                sparklineData={revenueSeries}
                tooltip={formatMessage({ id: 'tooltip.totalRevenue' })}
                deltaPct={toNullablePct(
                  stats?.revenue_delta_pct,
                  'admin-reports toNullablePct revenue_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalRevenue}
                  />
                }
              />
              <DashboardKpiTile
                icon={<BanknotesIcon />}
                label={<FormattedMessage id="reports.totalCommissions" />}
                loading={statsLoading}
                sparklineData={commissionsSeries}
                tooltip={formatMessage({ id: 'tooltip.totalCommissions' })}
                deltaPct={toNullablePct(
                  stats?.commissions_delta_pct,
                  'admin-reports toNullablePct commissions_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalCommissions}
                  />
                }
              />
              <DashboardKpiTile
                icon={<UsersIcon />}
                label={<FormattedMessage id="reports.activePartners" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.activePartners' })}
                value={<FormattedNumber value={stats?.active_partners ?? 0} />}
                deltaPct={toNullablePct(
                  stats?.active_partners_delta_pct,
                  'admin-reports toNullablePct active_partners_delta_pct:',
                )}
              />
            </div>

            <PerformanceChart
              controls={granularityToggle}
              data={chartData}
              icon={<PresentationChartLineIcon />}
              title={<FormattedMessage id="dashboard.performance" />}
              totals={[
                {
                  id: 'revenue',
                  label: <FormattedMessage id="admin.dashboard.revenueThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalRevenue}
                    />
                  ),
                },
                {
                  id: 'commissions',
                  label: <FormattedMessage id="admin.dashboard.commissionsThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalCommissions}
                    />
                  ),
                },
              ]}
            />

            <StatsCard
              icon={<TableCellsIcon />}
              title={<FormattedMessage id="reports.detailedBreakdown" />}
            >
              <DataTable
                columns={columns}
                data={rows}
                // A period report is read as one list, so the pager only appears past 50 rows.
                // `pageSizeOptions` has to say so too: the pager shows when the rows exceed the
                // SMALLEST offered size, so leaving the default [10, 25, 50] here put an inert
                // pager and a rows-per-page picker under every 11-to-50-row report.
                defaultPageSize={50}
                getRowId={(row: AffiliateAnalyticsRowSchema & { id: string }) => row.id}
                loading={analyticsLoading}
                pageSizeOptions={[50]}
                onRowClick={(row) => {
                  // Drill into the orders behind this period: both sides
                  // filter on the payment date, so the orders list shows
                  // exactly what this row counted.
                  const [from, to] = periodWindow(row.period_start, granularity)
                  void navigate({ to: '/admin/orders', search: { from, to } })
                }}
              />
            </StatsCard>
          </>
        )}
      </Page.Content>
    </Page>
  )
}
